import { createTheme, type PaletteOptions, type Theme } from '@mui/material'
import { components } from './components'
import { snackbarStyles } from './snackbar'
import { borders } from './tokens/borders'
import { legacyPalette } from './tokens/legacyPalette'
import { darkPalette, lightPalette } from './tokens/palette'
import { radii } from './tokens/radii'
import { darkShadows, lightShadows } from './tokens/shadows'
import { spacing } from './tokens/spacing'
import { strokeWidth } from './tokens/strokeWidth'
import { typography, type TokenTypographyVariants } from './tokens/typography'

type TokenPalette = typeof lightPalette | typeof darkPalette
type CustomShadows = typeof lightShadows | typeof darkShadows

// Only the token variants — the legacy and MUI built-in names in
// `tokens/typography.ts` are deliberately left undeclared so they aren't offered
// as `variant` values while call sites migrate off them.
type CustomVariants = TokenTypographyVariants

type LeafPaths<T, Prefix extends string = ''> = {
  [K in keyof T & (string | number)]: T[K] extends string ? `${Prefix}${K}` : LeafPaths<T[K], `${Prefix}${K}.`>
}[keyof T & (string | number)]

type TextColorPaths = LeafPaths<typeof lightPalette.text, 'text.'>

type TokenColorPath = LeafPaths<typeof lightPalette>

// Legacy palette paths stay suggested while call sites still use those values —
// once those are dropped, delete LegacyPaletteColor from SuggestedSxColor so
// only token paths are offered.
type LegacyPaletteColor = LeafPaths<typeof legacyPalette>
type SuggestedSxColor = LegacyPaletteColor | TokenColorPath

// `string & {}` accepts any CSS colour or palette path without adding it to
// autocomplete, so only SuggestedSxColor members show up as suggestions.
type SxColorValue = SuggestedSxColor | (string & {})

type BorderTokenPath = LeafPaths<typeof borders>
type SxBorderValue = number | BorderTokenPath | (string & {})

declare module '@mui/material/styles' {
  interface Theme {
    // `shadows` is MUI's fixed 25-slot elevation tuple, so the token shadows
    // live under `customShadows` instead.
    customShadows: CustomShadows
    strokeWidth: typeof strokeWidth
    borders: typeof borders
  }
  interface ThemeOptions {
    customShadows?: CustomShadows
    strokeWidth?: typeof strokeWidth
    borders?: typeof borders
  }
  interface Palette {
    bg: TokenPalette['bg']
    fg: TokenPalette['fg']
    border: TokenPalette['border']
    effects: TokenPalette['effects']
    shadows: TokenPalette['shadows']
    dataViz: TokenPalette['dataViz']
  }
  interface PaletteOptions {
    bg?: TokenPalette['bg']
    fg?: TokenPalette['fg']
    border?: TokenPalette['border']
    effects?: TokenPalette['effects']
    shadows?: TokenPalette['shadows']
    dataViz?: TokenPalette['dataViz']
  }
  // The token text colours merge into MUI's own `palette.text` type — the
  // member-by-member spelling is needed because an interface can't extend the
  // light|dark union.
  interface TypeText {
    default: TokenPalette['text']['default']
    muted: TokenPalette['text']['muted']
    subtle: TokenPalette['text']['subtle']
    inverted: TokenPalette['text']['inverted']
    link: TokenPalette['text']['link']
    brand: TokenPalette['text']['brand']
    success: TokenPalette['text']['success']
    danger: TokenPalette['text']['danger']
    warning: TokenPalette['text']['warning']
    info: TokenPalette['text']['info']
    ai: TokenPalette['text']['ai']
    utility: TokenPalette['text']['utility']
  }
  interface TypographyVariants extends CustomVariants {
    weights: typeof typography.weights
  }
  interface TypographyVariantsOptions extends Partial<CustomVariants> {
    weights?: typeof typography.weights
  }
}

declare module '@mui/system' {
  interface BreakpointOverrides {
    xxl: true
  }

  // sx resolves these properties against theme.palette at runtime, so
  // palette paths already work as values. Members added here replace the
  // csstype ones (AllSystemCSSProperties omits StandardCSSProperties keys that
  // OverwriteCSSProperties declares), which is what makes the paths appear in
  // autocomplete — and drops csstype's named-colour suggestions. The `bgcolor`
  // alias is pinned by AliasesCSSProperties and can't be widened — use
  // backgroundColor for suggestions.
  interface OverwriteCSSProperties {
    color?: SxColorValue
    backgroundColor?: SxColorValue
    borderColor?: SxColorValue
    borderTopColor?: SxColorValue
    borderRightColor?: SxColorValue
    borderBottomColor?: SxColorValue
    borderLeftColor?: SxColorValue
    outlineColor?: SxColorValue
    // `border` itself is pinned by @mui/system's own OverwriteCSSProperties
    // declaration and can't be widened — stroke tokens still work there via
    // csstype's `string & {}`, just without autocomplete.
    borderTop?: SxBorderValue
    borderRight?: SxBorderValue
    borderBottom?: SxBorderValue
    borderLeft?: SxBorderValue
  }

  interface Shape {
    'radius-none': string
    'radius-xxs': string
    'radius-xs': string
    'radius-sm': string
    'radius-md': string
    'radius-lg': string
    'radius-xl': string
    'radius-2xl': string
    'radius-3xl': string
    'radius-4xl': string
    'radius-5xl': string
    'radius-full': string
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides extends Record<keyof CustomVariants, true> {
    // Disallows usage of MUI's default variants to encourage use of our variants
    h1: false
    h2: false
    h3: false
    h4: false
    h5: false
    h6: false
    subtitle1: false
    subtitle2: false
    body1: false
    body2: false
    caption: false
    button: false
    overline: false
  }

  interface TypographyPropsColorOverrides extends Record<TextColorPaths, true> {
    // Disallows usage of MUI's default colors to encourage use of text.*
    // TODO - uncomment as these are tidied up
    //     primary: false
    //     secondary: false
    //     success: false
    //     error: false
    //     info: false
    //     warning: false
    textPrimary: false
    textSecondary: false
    textDisabled: false
  }
}

export const theme = createTheme({
  spacing,
  typography,
  // MUI replaces `values` wholesale rather than merging, so its own five have to be restated
  // alongside xxl.
  breakpoints: { values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536, xxl: 1920 } },
  shape: { borderRadius: 4, ...radii },
  palette: {
    // For backwards compatibility
    ...legacyPalette,
    ...lightPalette,
    // Transition hack
    primary: {
      main: lightPalette.bg.brand.default,
      dark: lightPalette.bg.brand.hover,
      light: lightPalette.bg.brand.muted,
      [100]: lightPalette.bg.brand.mutedHover,
      contrastText: lightPalette.text.brand.onBrand,
    },
    text: {
      ...lightPalette.text,
      // Legacy aliases for the token values
      primary: lightPalette.text.default,
      secondary: lightPalette.text.muted,
    },
    warning: {
      ...legacyPalette.warning,
      main: lightPalette.text.warning.default,
    },
    background: {
      // MUI components we haven't themed directly (ListSubheader, Slider, Autocomplete, ...)
      // fall back to this internally — keep it in sync with MuiPaper's own background.
      default: lightPalette.bg.surface.subtle,
      paper: lightPalette.bg.surface.elevated,
    },
    // Divider, ListItem and Table read `palette.divider` internally, so it cannot be dropped —
    // it is aliased onto the token instead. Application code must use `border.subtle` directly.
    divider: lightPalette.border.subtle,
    mode: 'light',
  } as PaletteOptions,
  customShadows: lightShadows,
  strokeWidth,
  borders,
  components,
})

export const darkTheme = createTheme(theme, {
  palette: {
    // For backwards compatibility
    ...legacyPalette,
    ...darkPalette,
    primary: {
      main: darkPalette.bg.brand.default,
      dark: darkPalette.bg.brand.hover,
      light: darkPalette.bg.brand.muted,
      [100]: darkPalette.bg.brand.mutedHover,
      contrastText: darkPalette.text.brand.onBrand,
    },
    text: {
      ...darkPalette.text,
      // Legacy aliases for the token values
      primary: darkPalette.text.default,
      secondary: darkPalette.text.muted,
    },
    warning: {
      ...legacyPalette.warning,
      main: darkPalette.text.warning.default,
    },
    background: {
      default: darkPalette.bg.surface.subtle,
      paper: darkPalette.bg.surface.elevated,
    },
    divider: darkPalette.border.subtle,
    mode: 'dark',
  } as PaletteOptions,
  customShadows: darkShadows,
})

export const globalStyles = (theme: Theme) => ({
  body: {
    // Important to override globals.css - remove that later
    backgroundColor: `${theme.palette.bg.surface.subtle} !important`,
    color: theme.palette.text.default,
    // Important to override globals.css - remove that later
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  main: {
    // Important to override globals.css - remove that later
    backgroundColor: `${theme.palette.bg.surface.subtle} !important`,
  },
  html: {
    // Important to override globals.css - remove that later
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  // Only required while we have globals.css
  button: {
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  ...snackbarStyles(theme),
})
